import { Box, Card, Typography, Chip, Stack } from "@compass/core";
import { Button } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import BugReportIcon from "@mui/icons-material/BugReport";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import { NotificationDto } from "generated/openApi";
import { formatDate } from "@compass/core";

interface AppTopBarNotificationsCardProps {
    notification: NotificationDto;
}

export const AppTopBarNotificationsCard = ({
    notification,
}: AppTopBarNotificationsCardProps) => {
    const theme = useTheme();

    return (
        <Box sx={{ position: "relative", pb: 3 }}>
            {/* Timeline Dot */}
            <Box
                sx={{
                    position: "absolute",
                    left: 12,
                    top: 8,
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    bgcolor: theme.palette.primary.main,
                    border: "3px solid",
                    borderColor: theme.palette.background.paper,
                    zIndex: 1,
                }}
            />
            {/* Content offset from timeline */}
            <Box sx={{ pl: 6 }}>
                <Typography
                    variant="caption"
                    sx={{ fontWeight: 600, color: theme.palette.text.secondary }}
                >
                    {formatDate(notification.createdDate)}
                </Typography>
                {/* Notification Card */}
                <Card
                    sx={{
                        mt: 1,
                        borderLeft: "4px solid",
                        borderLeftColor: theme.palette.primary.main,
                        boxShadow: theme.palette.shadow.elevatedCard,
                    }}
                >
                    <Box sx={{ p: 2 }}>
                        {/* Title Row */}
                        <Stack direction="row" alignItems="center" spacing="sm" flexWrap="wrap">
                            <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: 700, flex: 1 }}
                            >
                                {notification.title}
                            </Typography>
                            {notification.isBug && (
                                <Chip
                                    icon={<BugReportIcon />}
                                    label="Bug Fix"
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                />
                            )}
                        </Stack>
                        {/* Content */}
                        <Typography
                            variant="body2"
                            sx={{ mt: 1, lineHeight: 1.6, color: theme.palette.text.secondary }}
                        >
                            {notification.content}
                        </Typography>
                        {/* User Manual Link */}
                        {notification.userManualLink && (
                            <Box sx={{ mt: 1.5 }}>
                                <Button
                                    component="a"
                                    href={notification.userManualLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    startIcon={<MenuBookIcon />}
                                    size="small"
                                    variant="text"
                                >
                                    View User Manual
                                </Button>
                            </Box>
                        )}
                    </Box>
                </Card>
            </Box>
        </Box>
    );
};